<script setup lang="ts">
import { computed, ref, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import { useGetAllMetadataDetectors } from "@/stores/queries/metadata.query";
import { useGetFieldObservations } from "@/stores/queries/field-observations.query";
import { useNavigate } from "@/composable/useNavigate";
import { getGpsData, googleUrl } from "@/components/diagnostics/utils";
import type { Diagnostic, PaginationParams } from "@/types";
import type { RulesProps } from "@/composable/useValidation";
import EditableDiagnosticData from "@/components/diagnostics/diagnostic-drawer/EditableDiagnosticData.vue";
import DRBtn from "@/components/UI/DRBtn.vue";

/** Localisation de l'ouvrage (carte, accès, GPS) et accès à l'inspection. */

const props = defineProps<{
  diagnostic: Diagnostic;
}>();

const emit = defineEmits<{
  (
    e: "save",
    values: { gps_position?: string; access_instructions?: string }
  ): void;
}>();

const { t } = useTranslation();
const { navigateFieldObservations } = useNavigate();

const diagnosticId = computed(() => props.diagnostic.id);

// Observations terrain — détermine le libellé du bouton d'inspection
const { data: fieldObservations } = useGetFieldObservations(
  toRef(diagnosticId)
);
const hasObservations = computed(
  () => (fieldObservations.value?.length ?? 0) > 0
);

// Carte
const gpsData = computed(() => getGpsData(props.diagnostic));

const viewOptions = [
  {
    label: "ign",
    value: "GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2::GEOPORTAIL:OGC:WMTS(1)",
  },
  {
    label: "photos",
    value: "ORTHOIMAGERY.ORTHOPHOTOS::GEOPORTAIL:OGC:WMTS(1)",
  },
  {
    label: "cadastral",
    value: "CADASTRALPARCELS.PARCELLAIRE_EXPRESS::GEOPORTAIL:OGC:WMTS(1)",
  },
];

const view = ref(viewOptions[0].value);

const portailSrc = computed(() => {
  if (!gpsData.value) return;
  return `https://www.geoportail.gouv.fr/embed/visu.html?c=${gpsData.value.longitude},${gpsData.value.latitude}&z=18&l0=${view.value}&permalink=yes`;
});

// Détecteurs de métadonnées (suggestions IA sur accès / GPS)
const pagination = ref<PaginationParams>({
  skip: 0,
  take: 100,
  sortBy: "createdAt",
  order: "desc",
});

const { data: defaultMetadataDetectors } = useGetAllMetadataDetectors(
  pagination,
  ref({ isDefault: true })
);

const suggestions = computed(() => ({ ...props.diagnostic.suggestions }));

const findMetadataDetector = (slug: string) =>
  defaultMetadataDetectors.value?.find((detector) => detector.slug === slug);

const metadataDetectors = computed(() => ({
  gps_position: findMetadataDetector("gps_position"),
  access_instructions: findMetadataDetector("access_instructions"),
}));

const gpsRules: RulesProps[] = [
  {
    validator: (value: string) => {
      if (!value) return true;
      const regex =
        /^-?([1-8]?[1-9]|[1-9]0)\.{1}\d{1,6},\s*-?((1[0-7][0-9])|([1-9]?[0-9]))\.{1}\d{1,6}$/;
      return regex.test(value);
    },
    message: "Le format doit être : ###.######, ###.######",
  },
];
</script>

<template>
  <section class="diagnostic-block">
    <!-- Pas de titre : la description suffit à situer la section -->
    <p class="block-description">
      {{ t("report:diagnostics.inspectionDescription") }}
    </p>

    <!-- Consignes d'accès et coordonnées, sans séparateur -->
    <div class="fields">
      <EditableDiagnosticData
        v-if="metadataDetectors.access_instructions"
        :model-value="diagnostic.access_instructions"
        :suggestion="suggestions.access_instructions"
        :metadata-detector="metadataDetectors.access_instructions"
        :placeholder="t('report:set.access.intructions')"
        textarea
        @update:model-value="emit('save', { access_instructions: $event })"
      />

      <EditableDiagnosticData
        v-if="metadataDetectors.gps_position"
        :model-value="diagnostic.gps_position"
        :suggestion="suggestions.gps_position"
        :metadata-detector="metadataDetectors.gps_position"
        :placeholder="t('report:set.coordinate.format')"
        :rules="gpsRules"
        @update:model-value="emit('save', { gps_position: $event })"
      />
    </div>

    <!-- Carte, seulement si l'ouvrage est localisé -->
    <div v-if="gpsData" class="map-container">
      <iframe
        class="map-iframe"
        frameborder="0"
        scrolling="no"
        marginheight="0"
        marginwidth="0"
        sandbox="allow-forms allow-scripts allow-same-origin"
        :src="portailSrc"
        allowfullscreen
      />
      <div class="map-controls">
        <el-select
          v-model="view"
          :placeholder="t('report:map.select.view')"
          size="small"
          style="width: 200px"
        >
          <el-option
            v-for="option in viewOptions"
            :key="option.value"
            :label="
              t(`report:diagnostic.map.option.${option.label.toLowerCase()}`)
            "
            :value="option.value"
          />
        </el-select>
      </div>
    </div>

    <!-- Actions, sous la carte -->
    <div class="actions">
      <DRBtn
        icon="bi-clipboard2-pulse"
        :label="
          t(
            hasObservations
              ? 'report:diagnostics.continueInspection'
              : 'report:diagnostics.startInspection'
          )
        "
        @click="navigateFieldObservations(diagnostic.id)"
      />
      <a v-if="gpsData" :href="googleUrl(gpsData)" target="_blank">
        <DRBtn
          variant="outline"
          icon="bi-map"
          :label="t('report:diagnostics.access.link.googlemaps')"
        />
      </a>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.diagnostic-block {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.block-description {
  font-size: 13px;
  color: var(--bs-secondary-color);
  margin: 0;
}

.fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.map-container {
  width: 100%;
}

.map-iframe {
  display: block;
  width: 100%;
  height: 320px;
  border: none;
  border-radius: 12px;
}

.map-controls {
  display: flex;
  justify-content: center;
  padding-top: 10px;
}

/* Sur petit écran, les deux actions prennent toute la largeur, l'une sous l'autre */
@media (max-width: 767.98px) {
  .actions {
    flex-direction: column;
    align-items: stretch;
  }

  .actions a,
  .actions :deep(.btn) {
    width: 100%;
  }
}
</style>
